import { useEffect, useRef, useState } from 'react';
import {
    Alert,
    Box,
    Button,
    Chip,
    CircularProgress,
    MenuItem,
    Paper,
    Stack,
    Table,
    TableBody,
    TableCell,
    TableContainer,
    TableHead,
    TableRow,
    TextField,
    Typography,
} from '@mui/material';
import {
    Check as AcceptIcon,
    Close as RejectIcon,
    Refresh as RefreshIcon,
    Delete as DeleteIcon,
    Download as DownloadIcon,
    Upload as UploadIcon,
} from '@mui/icons-material';
import {
    downloadReportMatchingPreferences,
    deleteReportMatchingPreference,
    getReportMatchingPreferences,
    saveReportMatchingPreference,
    uploadReportMatchingPreferences,
} from '../services/api';

function ReportMatchingPreferencesPage() {
    const [preferences, setPreferences] = useState([]);
    const [search, setSearch] = useState('');
    const [statusFilter, setStatusFilter] = useState('all');
    const [loading, setLoading] = useState(true);
    const [savingId, setSavingId] = useState('');
    const [error, setError] = useState('');
    const [notice, setNotice] = useState('');
    const [noticeSeverity, setNoticeSeverity] = useState('success');
    const [transferBusy, setTransferBusy] = useState(false);
    const uploadInputRef = useRef(null);

    const getMatchTypeLabel = (preference) => {
        const rawType = String(preference?.context?.match_type || '').trim().toLowerCase();
        if (rawType === 'manual') return 'Manual';
        if (rawType.includes('semantic')) return 'Semantic';
        if (rawType) return 'Exact';
        return '-';
    };

    const formatScopedFieldLabel = (field, context = {}, side = 'legacy') => {
        if (!field) return '-';
        const reportName = String(
            field.report_name
            || context?.[`${side}_report_name`]
            || ''
        ).trim();
        const tableName = String(
            field.table_name
            || context?.[`${side}_table`]
            || ''
        ).trim();
        const physicalName = String(field.physical_name || '').trim();
        return [reportName, tableName, physicalName].filter(Boolean).join('.') || physicalName || '-';
    };

    const loadPreferences = async () => {
        try {
            setLoading(true);
            setError('');
            setNotice('');
            const response = await getReportMatchingPreferences({
                status: statusFilter === 'all' ? undefined : statusFilter,
                search: search || undefined,
                limit: 1000,
            });
            setPreferences(response?.preferences || []);
        } catch (err) {
            setError(err.response?.data?.detail || 'Failed to load report matching preferences');
        } finally {
            setLoading(false);
        }
    };

    useEffect(() => {
        loadPreferences();
    }, [statusFilter]);

    const handleApplySearch = async () => {
        await loadPreferences();
    };

    const handleUpdateStatus = async (preference, status) => {
        try {
            setSavingId(preference.id);
            setError('');
            setNotice('');
            await saveReportMatchingPreference({
                legacy_logical: preference.legacy_field?.logical_name || '',
                legacy_physical: preference.legacy_field?.physical_name || '',
                legacy_report_name: preference.legacy_field?.report_name || '',
                legacy_table: preference.legacy_field?.table_name || '',
                catalyst_logical: preference.catalyst_field?.logical_name || '',
                catalyst_physical: preference.catalyst_field?.physical_name || '',
                catalyst_report_name: preference.catalyst_field?.report_name || '',
                catalyst_table: preference.catalyst_field?.table_name || '',
                column_type: preference.legacy_field?.column_type || 'base',
                status,
                source: 'manual_correction',
                notes: preference.notes || '',
                decided_by: 'user',
                context: preference.context || {},
            });
            await loadPreferences();
        } catch (err) {
            setError(err.response?.data?.detail || 'Failed to update preference');
        } finally {
            setSavingId('');
        }
    };

    const handleDeletePreference = async (preference) => {
        const confirmed = window.confirm(
            `Delete this preference permanently?\n\nLegacy: ${preference.legacy_field?.logical_name || '-'}\nCatalyst: ${preference.catalyst_field?.logical_name || '-'}\n\nThis removes the full preference trace and history.`
        );
        if (!confirmed) {
            return;
        }

        try {
            setSavingId(preference.id);
            setError('');
            setNotice('');
            await deleteReportMatchingPreference(preference.id);
            await loadPreferences();
        } catch (err) {
            setError(err.response?.data?.detail || 'Failed to delete preference');
        } finally {
            setSavingId('');
        }
    };

    const handleDownload = async () => {
        try {
            setTransferBusy(true);
            setError('');
            const { blob, filename } = await downloadReportMatchingPreferences();
            const url = window.URL.createObjectURL(blob);
            const link = document.createElement('a');
            link.href = url;
            link.download = filename || 'report_matching_preferences.json';
            document.body.appendChild(link);
            link.click();
            link.remove();
            window.URL.revokeObjectURL(url);
            setNoticeSeverity('success');
            setNotice('Report matching preferences downloaded.');
        } catch (err) {
            setError(err.response?.data?.detail || 'Failed to download report matching preferences');
        } finally {
            setTransferBusy(false);
        }
    };

    const handleUploadClick = () => {
        uploadInputRef.current?.click();
    };

    const handleUploadFile = async (event) => {
        const [file] = Array.from(event.target.files || []);
        event.target.value = '';
        if (!file) {
            return;
        }

        try {
            setTransferBusy(true);
            setError('');
            setNotice('');
            const summary = await uploadReportMatchingPreferences(file);
            await loadPreferences();
            const errorCount = Array.isArray(summary?.errors) ? summary.errors.length : 0;
            setNoticeSeverity(errorCount ? 'warning' : 'success');
            setNotice(
                `Imported ${summary?.imported || 0} preferences (${summary?.created || 0} created, ${summary?.updated || 0} updated, ${summary?.skipped || 0} skipped).`
                + (errorCount ? ` ${errorCount} record(s) reported errors.` : '')
            );
        } catch (err) {
            setError(err.response?.data?.detail || 'Failed to upload report matching preferences');
        } finally {
            setTransferBusy(false);
        }
    };

    return (
        <Box>
            <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 3, gap: 2, flexWrap: 'wrap' }}>
                <Box>
                    <Typography variant="h5" fontWeight={700}>User Preferences</Typography>
                    <Typography variant="body2" color="text.secondary">
                        Global report-matching decisions reused across workspaces and projects.
                    </Typography>
                </Box>
                <Stack direction="row" spacing={1} flexWrap="wrap">
                    <input
                        ref={uploadInputRef}
                        type="file"
                        accept=".json,application/json"
                        hidden
                        onChange={handleUploadFile}
                    />
                    <Button
                        variant="outlined"
                        startIcon={<DownloadIcon />}
                        onClick={handleDownload}
                        disabled={loading || transferBusy}
                    >
                        Download
                    </Button>
                    <Button
                        variant="outlined"
                        startIcon={<UploadIcon />}
                        onClick={handleUploadClick}
                        disabled={loading || transferBusy}
                    >
                        Upload
                    </Button>
                    <Button
                        variant="outlined"
                        startIcon={<RefreshIcon />}
                        onClick={loadPreferences}
                        disabled={loading || transferBusy}
                    >
                        Refresh
                    </Button>
                </Stack>
            </Box>

            <Stack direction={{ xs: 'column', md: 'row' }} spacing={2} mb={3}>
                <TextField
                    label="Search"
                    value={search}
                    onChange={(event) => setSearch(event.target.value)}
                    onKeyDown={(event) => {
                        if (event.key === 'Enter') {
                            handleApplySearch();
                        }
                    }}
                    fullWidth
                    placeholder="Legacy or Catalyst logical/physical name"
                />
                <TextField
                    select
                    label="Status"
                    value={statusFilter}
                    onChange={(event) => setStatusFilter(event.target.value)}
                    sx={{ minWidth: 180 }}
                >
                    <MenuItem value="all">All</MenuItem>
                    <MenuItem value="accepted">Accepted</MenuItem>
                    <MenuItem value="rejected">Rejected</MenuItem>
                </TextField>
                <Button variant="contained" onClick={handleApplySearch} disabled={loading}>
                    Search
                </Button>
            </Stack>

            {error && <Alert severity="error" sx={{ mb: 2 }}>{error}</Alert>}
            {notice && <Alert severity={noticeSeverity} sx={{ mb: 2 }}>{notice}</Alert>}

            <Paper variant="outlined" sx={{ borderRadius: 3, overflow: 'hidden' }}>
                {loading ? (
                    <Box sx={{ py: 8, display: 'flex', justifyContent: 'center' }}>
                        <CircularProgress />
                    </Box>
                ) : (
                    <TableContainer sx={{ maxHeight: '70vh' }}>
                        <Table stickyHeader size="small">
                            <TableHead>
                                <TableRow>
                                    <TableCell sx={{ fontWeight: 700 }}>Legacy Source</TableCell>
                                    <TableCell sx={{ fontWeight: 700 }}>Catalyst Mapping</TableCell>
                                    <TableCell sx={{ fontWeight: 700 }}>Field Type</TableCell>
                                    <TableCell sx={{ fontWeight: 700 }}>Match Type</TableCell>
                                    <TableCell sx={{ fontWeight: 700, textAlign: 'center' }}>Actions</TableCell>
                                </TableRow>
                            </TableHead>
                            <TableBody>
                                {preferences.map((preference) => (
                                    <TableRow key={preference.id} hover>
                                        <TableCell>
                                            <Typography variant="body2" fontWeight={600}>
                                                {formatScopedFieldLabel(preference.legacy_field, preference.context, 'legacy')}
                                            </Typography>
                                            <Typography variant="caption" color="text.secondary">
                                                {preference.legacy_field?.logical_name || '-'}
                                            </Typography>
                                        </TableCell>
                                        <TableCell>
                                            <Typography variant="body2" fontWeight={600}>
                                                {formatScopedFieldLabel(preference.catalyst_field, preference.context, 'catalyst')}
                                            </Typography>
                                            <Typography variant="caption" color="text.secondary">
                                                {preference.catalyst_field?.logical_name || '-'}
                                            </Typography>
                                        </TableCell>
                                        <TableCell>{preference.legacy_field?.column_type || '-'}</TableCell>
                                        <TableCell>
                                            <Chip size="small" variant="outlined" label={getMatchTypeLabel(preference)} />
                                        </TableCell>
                                        <TableCell align="center">
                                            <Stack direction="row" spacing={0.5} justifyContent="center">
                                                <Button
                                                    size="small"
                                                    variant={preference.status === 'accepted' ? 'contained' : 'outlined'}
                                                    color="success"
                                                    startIcon={savingId === preference.id ? <CircularProgress size={14} color="inherit" /> : <AcceptIcon />}
                                                    onClick={() => handleUpdateStatus(preference, 'accepted')}
                                                    disabled={savingId === preference.id}
                                                >
                                                    Accept
                                                </Button>
                                                <Button
                                                    size="small"
                                                    variant={preference.status === 'rejected' ? 'contained' : 'outlined'}
                                                    color="warning"
                                                    startIcon={savingId === preference.id ? <CircularProgress size={14} color="inherit" /> : <RejectIcon />}
                                                    onClick={() => handleUpdateStatus(preference, 'rejected')}
                                                    disabled={savingId === preference.id}
                                                >
                                                    Reject
                                                </Button>
                                                <Button
                                                    size="small"
                                                    variant="outlined"
                                                    color="error"
                                                    startIcon={savingId === preference.id ? <CircularProgress size={14} color="inherit" /> : <DeleteIcon />}
                                                    onClick={() => handleDeletePreference(preference)}
                                                    disabled={savingId === preference.id}
                                                >
                                                    Delete
                                                </Button>
                                            </Stack>
                                        </TableCell>
                                    </TableRow>
                                ))}
                                {!preferences.length && (
                                    <TableRow>
                                        <TableCell colSpan={5} align="center" sx={{ py: 6 }}>
                                            <Typography color="text.secondary">No user preferences found.</Typography>
                                        </TableCell>
                                    </TableRow>
                                )}
                            </TableBody>
                        </Table>
                    </TableContainer>
                )}
            </Paper>
        </Box>
    );
}

export default ReportMatchingPreferencesPage;
